import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, Filter, FlaskRound as Flask, Beaker, Lightbulb } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import RDProductCard from '../components/rd/RDProductCard';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';

interface RDProduct {
  id: string;
  user_id: string;
  name: string;
  description: string;
  category_id: string;
  stock_quantity: number;
  min_stock_level: number;
  image: string;
  sku: string;
  location: string;
  status: 'in_development' | 'testing' | 'prototype' | 'ready' | 'discontinued';
  project_code: string;
  researcher: string;
  phase: string;
  budget_allocated?: number;
  budget_spent?: number;
  estimated_value?: number;
  development_cost?: number;
  start_date: string;
  target_completion: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

const RDInventoryPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [rdProducts, setRdProducts] = useState<RDProduct[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('filter') || 'all');
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load R&D products from rd_inventory table
      const { data: rdProductsData, error: rdProductsError } = await supabase
        .from('rd_inventory')
        .select('*')
        .order('created_at', { ascending: false });

      if (rdProductsError) throw rdProductsError;
      setRdProducts(rdProductsData || []);

      // Load categories for filtering
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('*')
        .order('name');

      if (categoriesError) throw categoriesError;
      setCategories(categoriesData || []);
    } catch (error) {
      console.error('Error loading R&D products:', error);
      setError('Failed to load R&D products');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    const filter = searchParams.get('filter');
    if (filter) {
      setStatusFilter(filter);
      setIsFiltersVisible(true);
    }
  }, [searchParams]);
  
  // Helper function to check if R&D product is low stock
  const isLowStock = (product: any) => {
    return product.stock_quantity <= product.min_stock_level;
  };

  // Filter R&D products based on search and filters
  const filteredProducts = rdProducts.filter((product: RDProduct) => {
    const matchesSearch = 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.project_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.researcher.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.description?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    
    const matchesCategory = !categoryFilter || product.category_id === categoryFilter;
    
    const matchesStatus = 
      statusFilter === 'all' ||
      (statusFilter === 'low' && isLowStock(product)) ||
      (statusFilter === 'out' && product.stock_quantity === 0) ||
      (statusFilter === 'in_development' && product.status === 'in_development') ||
      (statusFilter === 'testing' && product.status === 'testing') ||
      (statusFilter === 'prototype' && product.status === 'prototype') ||
      (statusFilter === 'ready' && product.status === 'ready');
    
    return matchesSearch && matchesCategory && matchesStatus;
  });
  
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setSearchParams(params => {
      if (value === 'all') {
        params.delete('filter');
      } else {
        params.set('filter', value);
      }
      return params;
    });
  };
  
  const categoryOptions = [
    { value: '', label: 'All Categories' },
    ...categories.map(category => ({
      value: category.id,
      label: category.name,
    })),
  ];
  
  const statusOptions = [
    { value: 'all', label: 'All Status Levels' },
    { value: 'in_development', label: 'In Development' },
    { value: 'testing', label: 'Testing' },
    { value: 'prototype', label: 'Prototype' },
    { value: 'ready', label: 'Ready' },
    { value: 'low', label: 'Low Stock' },
    { value: 'out', label: 'Out of Stock' },
  ];
  
  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className="overflow-hidden rounded-lg">
        <div className="relative">
          <img
            src="https://images.pexels.com/photos/3825581/pexels-photo-3825581.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
            alt="Modern research laboratory with scientific equipment and testing apparatus"
            className="w-full h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30 flex items-center">
            <div className="px-8 text-white">
              <h1 className="text-3xl font-bold mb-2">R & D Inventory</h1>
              <p className="text-lg opacity-90">
                Advanced research and development product management
              </p>
              <div className="mt-4 flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Flask className="h-4 w-4 mr-2" />
                  <span>{rdProducts.length} R&D Products</span>
                </div>
                <div className="flex items-center">
                  <Beaker className="h-4 w-4 mr-2" />
                  <span>{rdProducts.filter(p => p.status === 'testing').length} In Testing</span>
                </div>
                <div className="flex items-center">
                  <Lightbulb className="h-4 w-4 mr-2" />
                  <span>{rdProducts.filter(p => p.status === 'in_development').length} In Development</span>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute top-4 right-4">
            <Link to="/rd-inventory/new">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Product for R&D
              </Button>
            </Link>
          </div>
        </div>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-600" />
          </div>
          <Input
            type="search"
            placeholder="Search R&D products, project codes, researchers..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
          />
        </div>
        <Button 
          variant="secondary" 
          className="w-full sm:w-auto"
          onClick={() => setIsFiltersVisible(!isFiltersVisible)}
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
        </Button>
      </div>
      
      {isFiltersVisible && (
        <div className="flex flex-col sm:flex-row gap-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex-1">
            <Select
              label="Category"
              options={categoryOptions}
              value={categoryFilter}
              onChange={(value) => setCategoryFilter(value)}
              fullWidth
            />
          </div>
          <div className="flex-1">
            <Select
              label="Status"
              options={statusOptions}
              value={statusFilter}
              onChange={handleStatusFilterChange}
              fullWidth
            />
          </div>
        </div>
      )}
      
      {filteredProducts.length === 0 ? (
        loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No R&D products found</p>
            <Link to="/rd-inventory/new">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First R&D Product
              </Button>
            </Link>
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map(product => (
            <RDProductCard
              key={product.id}
              product={product}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default RDInventoryPage;